import { defineStore } from 'pinia'
import stripeApi from '@/api/stripe'
import auth0Plugin from '@/setups/authSetup'
import { tracker } from '@/tracker'
import { differenceInMilliseconds, parseISO } from 'date-fns'
import {
  ApiKeyRevocationResult,
  type ApiKeyResponse,
  type ApiPartner,
  type BillingAddress,
  type CreateApiKeyBody,
  type Subscription
} from 'common'

const isForbidden = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const candidate = error as { status?: unknown; response?: { status?: unknown } }
  const status = candidate.status ?? candidate.response?.status
  return status === 403
}

let apiKeysRequestId = 0

const propertyReportID: string = import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT
const marketReportID: string = import.meta.env.VITE_STRIPE_MARKET_REPORTS_PRODUCT
const financialAnalysisID: string = import.meta.env.VITE_STRIPE_FINANCIAL_ANALYSIS_PRODUCT

export const useStripeStore = defineStore('stripe', {
  state: () => ({
    upcomingInvoice: null as {
      period_end: number
      period_start: number
      amount_due: number
      total: number
      discount: {
        name?: string | null | undefined
        amount: number
      } | null
      subtotal: number
      lines: {
        nickname?: string | null
        quantity: number | null
        description: string | null
        amount: number
      }[]
    } | null,
    apiKeys: [] as ApiKeyResponse[],
    apiKeysLoading: false,
    apiKeysError: false,
    apiPartners: [] as ApiPartner[],
    apiPartnersLoading: false,
    apiPartnersError: false,
    apiPartnersLoaded: false,
    apiKeysMutating: false,
    hasCustomerPortal: false,
    subscriptions: [] as Subscription[],
    childSubscriptions: [] as Subscription[],
    propertyReportTrialConsumed: null as boolean | null,
    subscriptionsLoading: false,

    // The auth0Plugin does not work on the reports server. This gives us a way
    // to manually set the products on the reports server.
    reportsProducts: [] as string[]
  }),
  getters: {
    hasAPI(): boolean {
      const apiProduct = import.meta.env.VITE_STRIPE_API_PROPERTY_DETAILS_PRODUCT
      return this.subscriptions.some((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === apiProduct)
      )
    },
    trialDaysLeft(): number | null {
      const propertySub = this.propertyReportSubscription
      if (propertySub?.trial_end) {
        const ms = differenceInMilliseconds(parseISO(propertySub.trial_end), new Date())
        return ms / (1000 * 60 * 60 * 24)
      }
      return null
    },
    marketTrialDaysLeft(): number | null {
      const marketSub = this.marketReportSubscription
      if (marketSub?.trial_end) {
        const ms = differenceInMilliseconds(parseISO(marketSub.trial_end), new Date())
        return ms / (1000 * 60 * 60 * 24)
      }
      return null
    },
    financialAnalysisTrialDaysLeft(): number | null {
      const financialAnalysisSub = this.financialAnalysisSubscription
      if (financialAnalysisSub?.trial_end) {
        const ms = differenceInMilliseconds(parseISO(financialAnalysisSub.trial_end), new Date())
        return ms / (1000 * 60 * 60 * 24)
      }
      return null
    },
    billedPerSeat: (state): boolean => {
      const sub = state.subscriptions.find((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === propertyReportID)
      )
      if (sub === undefined) {
        return false
      }

      return sub.per_seat
    },
    billedPerUnit: (state): boolean => {
      const sub = state.subscriptions.find((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === propertyReportID)
      )
      if (sub === undefined) {
        return false
      }

      return !sub.per_seat
    },
    propertyReportSubscription: (state): Subscription | null => {
      const sub = state.subscriptions.find((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === propertyReportID)
      )
      return sub || null
    },
    marketReportSubscription: (state): Subscription | null => {
      const sub = state.subscriptions.find((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === marketReportID)
      )
      return sub || null
    },
    financialAnalysisSubscription: (state): Subscription | null => {
      const sub = state.subscriptions.find((_) =>
        _.products.some((p: { product_id: string }) => p.product_id === financialAnalysisID)
      )
      return sub || null
    },
    products(): string[] {
      return this.reportsProducts.length
        ? this.reportsProducts
        : auth0Plugin.user.value?.products || []
    },
    hasPropertyReports(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT
      )
      return productIndex > -1
    },
    hasFinancialAnalysis(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_FINANCIAL_ANALYSIS_PRODUCT
      )
      return productIndex > -1
    },
    hasRevenueManagement(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_REVENUE_MANAGEMENT_PRODUCT
      )
      return productIndex > -1
    },
    hasMarketReports(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_MARKET_REPORTS_PRODUCT
      )
      return productIndex > -1
    },
    hasRentProjections(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_RENT_PROJECTIONS_PRODUCT
      )
      return productIndex > -1
    },
    hasQueryBuilder(): boolean {
      return this.products?.includes(import.meta.env.VITE_STRIPE_QUERY_BUILDER_PRODUCT) || false
    },
    hasRentRollParser(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_RENT_ROLL_PARSER_PRODUCT
      )
      return productIndex > -1
    },
    hasMarketFinder(): boolean {
      const productIndex = this.products.findIndex(
        (_: string) => _ === import.meta.env.VITE_STRIPE_MARKET_FINDER_PRODUCT
      )
      return productIndex > -1
    },
    hasLimitedAccess: (): boolean => {
      return auth0Plugin.user.value?.limitedAccess || false
    },
    propertyReportSeats: (): number => {
      return auth0Plugin.user.value?.propertyReportSeats || 0
    },
    hasBulkData(): boolean {
      return (
        this.products?.findIndex(
          (_: string) => _ === import.meta.env.VITE_STRIPE_BULK_DATA_PRODUCT
        ) > -1
      )
    },
    hasDevelopmentPipeline(): boolean {
      return (
        this.products?.includes(import.meta.env.VITE_STRIPE_DEVELOPMENT_PIPELINE_PRODUCT) || false
      )
    }
  },
  actions: {
    async getCustomerPortal() {
      const resp = await stripeApi.getCustomerPortal()
      this.hasCustomerPortal = resp !== null
      if (resp) {
        return resp.url
      }
      return null
    },
    async initChildSubscriptions() {
      this.childSubscriptions = await stripeApi.getChildSubscriptions()
    },
    async closeChildAccount(subscriptionId: string) {
      const newSub = await stripeApi.closeChildAccount(subscriptionId)
      if (newSub) {
        const idx = this.childSubscriptions.findIndex((_) => _.id === subscriptionId)
        this.childSubscriptions.splice(idx, 1, newSub)
      }
    },
    async keepChildAccount(subscriptionId: string) {
      const newSub = await stripeApi.keepChildAccount(subscriptionId)
      if (newSub) {
        const idx = this.childSubscriptions.findIndex((_) => _.id === subscriptionId)
        this.childSubscriptions.splice(idx, 1, newSub)
      }
    },
    async initUpcomingInvoice() {
      this.upcomingInvoice = await stripeApi.getUpcomingInvoice()
    },
    async initOrganizationApiKeys() {
      const requestId = ++apiKeysRequestId
      this.apiKeysLoading = true
      this.apiKeysError = false
      try {
        const apiKeys = await stripeApi.getOrganizationApiKeys()
        if (requestId === apiKeysRequestId) {
          this.apiKeys = apiKeys
        }
      } catch (error) {
        if (requestId !== apiKeysRequestId) {
          return
        }
        if (!isForbidden(error)) {
          console.error('Failed to initialize organization API keys', error)
        }
        this.apiKeys = []
        this.apiKeysError = !isForbidden(error)
      } finally {
        if (requestId === apiKeysRequestId) {
          this.apiKeysLoading = false
        }
      }
    },
    async initApiPartners() {
      if (this.apiPartnersLoading || this.apiPartnersLoaded) {
        return
      }
      this.apiPartnersLoading = true
      this.apiPartnersError = false
      try {
        this.apiPartners = await stripeApi.getApiPartners()
        this.apiPartnersLoaded = true
      } catch (error) {
        console.error('Failed to initialize API partners', error)
        this.apiPartners = []
        this.apiPartnersError = true
      } finally {
        this.apiPartnersLoading = false
      }
    },
    async createApiKey(body: CreateApiKeyBody) {
      this.apiKeysMutating = true
      try {
        const key = await stripeApi.createApiKey(body)
        if (this.apiKeysError || this.apiKeysLoading) {
          // The list failed or is still loading, so reload it instead of showing only the new key
          void this.initOrganizationApiKeys()
        } else {
          this.apiKeys.unshift(key)
        }
        return key
      } finally {
        this.apiKeysMutating = false
      }
    },
    async renameApiKey(key: string, name: string) {
      this.apiKeysMutating = true
      try {
        const updated = await stripeApi.renameApiKey({ key, name })
        const index = this.apiKeys.findIndex((apiKey) => apiKey.key === key)
        if (index !== -1) {
          this.apiKeys.splice(index, 1, updated)
        }
        return updated
      } finally {
        this.apiKeysMutating = false
      }
    },
    async revokeApiKey(key: string) {
      this.apiKeysMutating = true
      try {
        const results = await stripeApi.revokeApiKeys([key])
        const result = results.find((item) => item.key === key)
        if (
          result?.status !== ApiKeyRevocationResult.status.REVOKED &&
          result?.status !== ApiKeyRevocationResult.status.ALREADY_REVOKED
        ) {
          throw new Error('API key not found.')
        }
        this.apiKeys = this.apiKeys.filter((apiKey) => apiKey.key !== key)
      } finally {
        this.apiKeysMutating = false
      }
    },
    async initCustomerSubscriptions() {
      this.subscriptionsLoading = true
      try {
        this.subscriptions = await stripeApi.getCustomerSubscriptions()
        tracker.setSubscriptions(
          this.subscriptions.flatMap((_) => _.products.map((p) => p.product_id))
        )
      } catch (e) {
        console.error('Failed to initialize customer subscriptions', e)
      } finally {
        this.subscriptionsLoading = false
      }
    },
    setPaymentMethod(card: string) {
      return stripeApi
        .setPaymentMethod(card)
        .then((response) => {
          return response
        })
        .catch((e) => {
          console.error(e)
          return null
        })
    },
    createPropertyReportsSubscription(card: string, coupon?: string, address?: BillingAddress) {
      tracker.subscribed('property_reports', coupon)
      return stripeApi.createPropertyReportsSubscription(card, coupon, address)
    },
    createMarketAnalyticsSubscription() {
      tracker.subscribed('market_reports')
      return stripeApi.createMarketAnalyticsSubscription()
    },
    createFinancialAnalysisSubscription() {
      tracker.subscribed('financial_analysis')
      return stripeApi.createFinancialAnalysisSubscription()
    },
    updateNumberPropertyReportsSeats(seats: number) {
      tracker.updatedSeats('property_reports', this.propertyReportSeats, seats)
      return stripeApi.updateNumberPropertyReportsSeats(seats)
    },
    async initPropertyReportTrialConsumed() {
      this.propertyReportTrialConsumed = await stripeApi.propertyReportTrialConsumed()
    },
    cancelSubscription() {
      return stripeApi.cancelSubscription()
    },
    renewSubscription() {
      return stripeApi.renewSubscription()
    }
  }
})
